import {useCallback, useEffect, useRef, useState} from "react"
import type {Dispatch, SetStateAction} from "react"

type PageResult<TItem> = {
   data: TItem[]
   totalCount: number
}

type FetchPageArgs<TCursor> = {
   pageSize: number
   cursor?: TCursor
}

type UsePaginatedListOptions<TItem, TCursor> = {
   pageSize: number
   fetchPage: (args: FetchPageArgs<TCursor>) => Promise<PageResult<TItem>>
   getCursor: (item: TItem) => TCursor
   getItemKey: (item: TItem) => string
   resetKey?: unknown
}

type LoadPageMode = "append" | "replace"

const mergeUniqueItems = <TItem>(
   currentItems: TItem[],
   nextItems: TItem[],
   getItemKey: (item: TItem) => string,
) => {
   const currentKeys = new Set(currentItems.map(getItemKey))
   return [...currentItems, ...nextItems.filter((item) => !currentKeys.has(getItemKey(item)))]
}

export function usePaginatedList<TItem, TCursor>({
   pageSize,
   fetchPage,
   getCursor,
   getItemKey,
   resetKey,
}: UsePaginatedListOptions<TItem, TCursor>) {
   const loadingRef = useRef(false)
   const requestIdRef = useRef(0)
   const [items, setItems] = useState<TItem[]>([])
   const [totalCount, setTotalCount] = useState<number | null>(null)
   const [isLoading, setIsLoading] = useState(false)
   const [isLoadingAll, setIsLoadingAll] = useState(false)
   const [hasError, setHasError] = useState(false)

   const loadedCount = items.length
   const displayTotal = totalCount ?? loadedCount
   const remainingCount = Math.max(displayTotal - loadedCount, 0)
   const hasMore = totalCount !== null && loadedCount < totalCount

   const setLoading = useCallback((value: boolean) => {
      loadingRef.current = value
      setIsLoading(value)
   }, [])

   const loadPage = useCallback(
      async (cursor?: TCursor, mode: LoadPageMode = "append") => {
         if (loadingRef.current) return

         const requestId = ++requestIdRef.current
         setLoading(true)
         setHasError(false)

         try {
            const response = await fetchPage({pageSize, cursor})

            if (requestId !== requestIdRef.current) return

            setTotalCount(response.totalCount)
            setItems((currentItems) =>
               mode === "replace"
                  ? response.data
                  : mergeUniqueItems(currentItems, response.data, getItemKey),
            )
         } catch {
            if (requestId === requestIdRef.current) setHasError(true)
         } finally {
            if (requestId === requestIdRef.current) setLoading(false)
         }
      },
      [fetchPage, getItemKey, pageSize, setLoading],
   )

   const loadFirstPage = useCallback(() => {
      void loadPage(undefined, "replace")
   }, [loadPage])

   const reset = useCallback(() => {
      requestIdRef.current += 1
      loadingRef.current = false
      setIsLoading(false)
      setIsLoadingAll(false)
      setItems([])
      setTotalCount(null)
      setHasError(false)
   }, [])

   const loadMore = useCallback(() => {
      const lastItem = items[items.length - 1]
      if (!lastItem || isLoading) return
      void loadPage(getCursor(lastItem))
   }, [getCursor, isLoading, items, loadPage])

   const loadAll = useCallback(() => {
      const lastItem = items[items.length - 1]
      if (!lastItem || isLoading || remainingCount === 0) return

      const requestId = ++requestIdRef.current
      const loadedItemKeys = new Set(items.map(getItemKey))
      let cursor = getCursor(lastItem)

      setLoading(true)
      setIsLoadingAll(true)
      setHasError(false)

      const loadRemainingPages = async () => {
         try {
            while (requestId === requestIdRef.current) {
               const response = await fetchPage({pageSize, cursor})
               if (requestId !== requestIdRef.current) return

               setTotalCount(response.totalCount)

               if (response.data.length === 0) {
                  if (loadedItemKeys.size < response.totalCount) setHasError(true)
                  return
               }

               response.data.forEach((item) => loadedItemKeys.add(getItemKey(item)))
               setItems((currentItems) => mergeUniqueItems(currentItems, response.data, getItemKey))

               if (loadedItemKeys.size >= response.totalCount) return

               const nextCursor = getCursor(response.data[response.data.length - 1])
               if (Object.is(nextCursor, cursor)) {
                  setHasError(true)
                  return
               }
               cursor = nextCursor
            }
         } catch {
            if (requestId === requestIdRef.current) setHasError(true)
         } finally {
            if (requestId === requestIdRef.current) {
               setIsLoadingAll(false)
               setLoading(false)
            }
         }
      }

      void loadRemainingPages()
   }, [fetchPage, getCursor, getItemKey, isLoading, items, pageSize, remainingCount, setLoading])

   useEffect(() => {
      reset()
      loadFirstPage()
   }, [loadFirstPage, reset, resetKey])

   return {
      items,
      setItems: setItems as Dispatch<SetStateAction<TItem[]>>,
      totalCount,
      displayTotal,
      remainingCount,
      hasMore,
      isLoading,
      isLoadingAll,
      hasError,
      loadFirstPage,
      loadMore,
      loadAll,
      reset,
   }
}
